import { forwardRef, useId, type ReactNode } from 'react';
import { Info, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface CampoDeBuscaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** O que o ⓘ abre ("Como buscar"). Sem ele, não há ⓘ. */
  ajuda?: ReactNode;
  /** Rótulo acessível do ⓘ. */
  ajudaRotulo?: string;
  /** Texto discreto dentro do campo, antes dos botões ("12 de 423"). */
  contagem?: string;
  autoFocus?: boolean;
}

/**
 * O campo de busca das telas de lista (docs/PLANO_BARRA_DE_LISTA.md): a lupa à
 * esquerda; à direita, embutidos, a contagem, o X para limpar e o ⓘ com a ajuda.
 * Nada fica fora do campo — a barra da lista dá a ele o resto da linha.
 *
 * `forwardRef` para o host mandar o foco ao campo depois da montagem.
 */
export const CampoDeBusca = forwardRef<HTMLInputElement, CampoDeBuscaProps>(function CampoDeBusca(
  { value, onChange, placeholder = 'Buscar…', className, ajuda, ajudaRotulo = 'Como buscar', contagem, autoFocus },
  ref,
) {
  // Espaço à direita para o que estiver embutido: cada botão tem 32px.
  const botoes = (value ? 1 : 0) + (ajuda ? 1 : 0);
  const contagemId = useId();
  return (
    <div className={cn('relative min-w-0', className)}>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-describedby={contagemId}
        autoFocus={autoFocus}
        className={cn(
          'h-11 pl-9',
          botoes === 0 ? 'pr-3' : botoes === 1 ? 'pr-10' : 'pr-[4.5rem]',
          contagem && (botoes === 0 ? 'pr-24' : botoes === 1 ? 'pr-32' : 'pr-40'),
        )}
        // Filtro não é trabalho: digitar aqui não trava o Esc de um modal
        // (ver `components/ui/discard-guard.tsx`).
        data-no-dirty=""
      />
      <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
        {/* Sempre montado: a região aria-live precisa existir antes de o texto mudar. */}
        <span
          id={contagemId}
          aria-live="polite"
          className="mr-1 whitespace-nowrap text-xs tabular-nums text-muted-foreground empty:mr-0"
        >
          {contagem}
        </span>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange('')}
            aria-label="Limpar busca"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        {ajuda && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={ajudaRotulo}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(24rem,calc(100vw-2rem))]">
              {ajuda}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
});
